import React, { useState, useEffect, useContext } from 'react';
import { useStoreCurrencyQuery } from '../../graphql/queries/storeCurrency.generated';
import { centsToPrice } from '@/utils/formatter';
import { InventoryCheck } from '../../views/InventoryCheckView';
import { IsAdmin, ViewerContext } from '../../providers/ViewerContextProvider';
import { Table } from 'reactstrap';

interface InventoryCheckBillSummaryProps {
  inventoryCheck: InventoryCheck;
  externalPickpackId: string;
  onDiscountChange: (discount: number) => void;
}

interface AggregatedEntry {
  type: 'Container' | 'UpperPart' | 'LowerPart';
  containerTypeId: number;
  containerTypeName: string;
  amount: number;
  costPerItem: number;
  totalCost: number;
}

const InventoryCheckBillSummary = ({
  inventoryCheck,
  externalPickpackId,
  onDiscountChange,
}: InventoryCheckBillSummaryProps) => {
  const { data: storeCurrencyData, loading } = useStoreCurrencyQuery({
    variables: { id: externalPickpackId },
    skip: !externalPickpackId,
  });
  const currency = storeCurrencyData?.store?.currency;
  const viewer = useContext(ViewerContext);
  const [aggregatedData, setAggregatedData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const discountPercentage = inventoryCheck?.discountPercentage || 0;

  useEffect(() => {
    const aggregated: AggregatedEntry[] = [];

    inventoryCheck.missingContainers
      .filter((c) => c.isBillable)
      .forEach((missingContainer) => {
        const containerTypePriceInfo =
          inventoryCheck.containerTypePriceInfos?.find(
            (info) => info.containerTypeId === missingContainer.typeId,
          );

        if (containerTypePriceInfo) {
          const existingEntry = aggregated.find(
            (entry) =>
              entry.containerTypeId ===
                containerTypePriceInfo.containerTypeId &&
              entry.type === 'Container',
          );

          if (existingEntry) {
            existingEntry.amount = existingEntry.amount + 1;
            existingEntry.totalCost =
              containerTypePriceInfo.storeLostCost * existingEntry.amount;
          } else {
            aggregated.push({
              type: 'Container',
              containerTypeId: missingContainer.typeId,
              containerTypeName: containerTypePriceInfo.containerTypeName,
              amount: 1,
              costPerItem: containerTypePriceInfo.storeLostCost,
              totalCost: containerTypePriceInfo.storeLostCost,
            });
          }
        } else {
          alert(
            'Error: could not find container type price info for container type id ' +
              missingContainer.typeId,
          );
        }
      });

    inventoryCheck.missingContainerParts.forEach((missingPart) => {
      const containerTypePriceInfo =
        inventoryCheck.containerTypePriceInfos?.find(
          (info) => info.containerTypeId === missingPart.typeId,
        );

      const entryType = missingPart.part === 1 ? 'UpperPart' : 'LowerPart';
      if (containerTypePriceInfo) {
        const existingEntry = aggregated.find(
          (entry) =>
            entry.containerTypeId === missingPart.typeId &&
            entry.type === entryType,
        );

        if (existingEntry) {
          existingEntry.amount = existingEntry.amount + missingPart.amount;
          existingEntry.totalCost =
            existingEntry.amount * existingEntry.costPerItem;
        } else {
          const costPerItem =
            entryType === 'UpperPart'
              ? containerTypePriceInfo.storeLostCostUpper
              : containerTypePriceInfo.storeLostCostLower;
          aggregated.push({
            type: entryType,
            containerTypeId: missingPart.typeId,
            containerTypeName: containerTypePriceInfo.containerTypeName,
            costPerItem,
            amount: missingPart.amount,
            totalCost: missingPart.amount * costPerItem,
          });
        }
      }
    });

    const totalCost = aggregated.reduce((acc, curr) => acc + curr.totalCost, 0);
    setAggregatedData(aggregated);
    setTotal(totalCost);
  }, [inventoryCheck]);

  if (loading || !currency) {
    return <div>Loading...</div>;
  }

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      onDiscountChange(value);
    }
  };

  console.log({ inventoryCheck, discountPercentage, aggregatedData, total });
  const discountAmount = (total * discountPercentage) / 100;
  const totalAfterDiscount = total - discountAmount;

  const isDiscountAllowed = IsAdmin(viewer);

  return (
    <div>
      <Table striped>
        <thead>
          <tr>
            <th>Container Type</th>
            <th>Amount</th>
            <th>Cost Per Item</th>
            <th>Sum</th>
          </tr>
        </thead>

        <tbody>
          {aggregatedData.map((entry: AggregatedEntry, index) => (
            <tr key={index}>
              <td>
                {entry.containerTypeName} - {entry.type}
              </td>
              <td className="numbers-formatted">{entry.amount}</td>
              <td className="numbers-formatted">
                {centsToPrice(entry.costPerItem, currency)}
              </td>
              <td className="numbers-formatted">
                {centsToPrice(entry.totalCost, currency)}
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr>
            <td colSpan={3} className="text-end">
              Total
            </td>
            <td className="numbers-formatted">
              {centsToPrice(total, currency)}
            </td>
          </tr>

          <tr>
            <td colSpan={3} className="text-end">
              Discount amount ({discountPercentage}%)
            </td>
            <td className="numbers-formatted">
              {centsToPrice(discountAmount, currency)}
            </td>
          </tr>

          <tr>
            <td colSpan={3} className="fw-bold text-end">
              Total after discount
            </td>

            <td className="fw-bold numbers-formatted">
              {centsToPrice(totalAfterDiscount, currency)}
            </td>
          </tr>
        </tfoot>
      </Table>

      {isDiscountAllowed && (
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            Discount (%)
          </span>

          <input
            type="number"
            className={
              'form-control ' + (!isDiscountAllowed ? 'is-invalid' : '')
            }
            id="discount-input"
            value={discountPercentage}
            onChange={handleDiscountChange}
            min="0"
            max="100"
            aria-label="Discount"
            aria-describedby="basic-addon1"
          />
        </div>
      )}
    </div>
  );
};

export default InventoryCheckBillSummary;
